import { create } from 'zustand';
import { registerMediaPipeSw } from '../features/movexBuilder/services/registerMediaPipeSw';

const LOCAL_WASM_PATH = '/wasm';
// The heavy task model is the closest equivalent to legacy MediaPipe model_complexity = 2.
const LOCAL_HEAVY_MODEL_PATH = '/models/pose_landmarker_heavy.task';

let initPromise = null;
let lastVideoTimestampMs = -1;

function resolveErrorMessage(error) {
  if (error && typeof error === 'object' && typeof error.message === 'string' && error.message) {
    return error.message;
  }

  return 'Failed to load MediaPipe Pose';
}

export const useMediaPipeStore = create((set, get) => ({
  poseLandmarker: '',
  imagePoseLandmarker: '',
  handLandMark: '',
  worker: null,
  detectionModel: null,
  isReady: false,
  isLoading: false,
  error: null,
  setWorker(value) {
    set({ worker: value });
  },
  addPoseLandMarker(data) {
    set({ poseLandmarker: data });
  },
  addHandPoseLandMarker(data) {
    set({ handLandMark: data });
  },
  setDetectModel(data) {
    set({ detectionModel: data });
  },
  getPoseLandmarker() {
    return get().poseLandmarker;
  },
  getImagePoseLandmarker() {
    return get().imagePoseLandmarker;
  },
  async initializePoseLandmark() {
    if (get().poseLandmarker && get().imagePoseLandmarker) {
      if (!get().isReady) {
        set({ isReady: true, isLoading: false, error: null });
      }
      return get().poseLandmarker;
    }

    if (initPromise) {
      return initPromise;
    }

    set({ isLoading: true, error: null });
    initPromise = (async () => {
      try {
        void registerMediaPipeSw().catch(() => {});

        const { FilesetResolver, PoseLandmarker } = await import('@mediapipe/tasks-vision');
        const vision = await FilesetResolver.forVisionTasks(LOCAL_WASM_PATH);
        const sharedOptions = {
          baseOptions: {
            modelAssetPath: LOCAL_HEAVY_MODEL_PATH,
            delegate: 'GPU',
          },
          numPoses: 1,
          minPoseDetectionConfidence: 0.9,
          minTrackingConfidence: 0.9,
          minPosePresenceConfidence: 0.9,
          outputSegmentationMasks: false,
          static_image_mode: false,
        };
        const [poseLandmarker, imagePoseLandmarker] = await Promise.all([
          PoseLandmarker.createFromOptions(vision, {
            ...sharedOptions,
            runningMode: 'VIDEO',
          }),
        ]);

        set({
          poseLandmarker,
          imagePoseLandmarker,
          isReady: true,
          isLoading: false,
          error: null,
        });
        return poseLandmarker;
      } catch (error) {
        initPromise = null;
        set({
          poseLandmarker: '',
          imagePoseLandmarker: '',
          isReady: false,
          isLoading: false,
          error: resolveErrorMessage(error),
        });
        return null;
      }
    })();

    return initPromise;
  },
  async ensureInitialized() {
    return get().initializePoseLandmark();
  },
  async analyzeFrame(imageSource) {
    const pose = get().imagePoseLandmarker || (await get().initializePoseLandmark());
    if (!pose) {
      return null;
    }

    return pose.detectForVideo(imageSource);
  },
  getNewVideoSessionBase() {
    // Returns a base timestamp for a new video session.
    // Using performance.now() ensures the base is always ahead of any previous
    // lastVideoTimestampMs, so callers can do (sessionBase + frame.timestampMs)
    // to get properly-spaced timestamps without conflicting with MediaPipe's
    // internal monotonic counter from the previous session.
    return Math.max(Math.ceil(performance.now()), lastVideoTimestampMs + 1);
  },
  async analyzeVideoFrame(videoSource, timestampMs) {
    const pose = get().poseLandmarker || (await get().initializePoseLandmark());
    if (!pose || !videoSource) {
      return null;
    }

    const requestedTimestampMs = Number(timestampMs);
    const safeTimestampMs = Number.isFinite(requestedTimestampMs)
      ? requestedTimestampMs
      : performance.now();
    const nextTimestampMs =
      safeTimestampMs > lastVideoTimestampMs ? safeTimestampMs : lastVideoTimestampMs + 1;
    lastVideoTimestampMs = nextTimestampMs;

    return pose.detectForVideo(videoSource, nextTimestampMs);
  },
}));
